import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Hourglass, KeyRound, Lock, ShieldAlert, ShieldCheck } from "lucide-react";
import { IDEVRA } from "@/data/idevra";
import { sfx } from "@/lib/sound";

const API = process.env.REACT_APP_BACKEND_URL;
const STORE_KEY = "idevra_vault_access";

export const readVaultAccess = () => {
  try {
    return JSON.parse(localStorage.getItem(STORE_KEY) || "null");
  } catch {
    return null;
  }
};
const storeAccess = (v) => localStorage.setItem(STORE_KEY, JSON.stringify(v));

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.06 } } };
const rise = {
  hidden: { opacity: 0, y: 16 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: [0.22, 1, 0.36, 1] } },
};

const FIELDS = [
  { id: "name", label: "FULL NAME *", type: "text" },
  { id: "email", label: "EMAIL *", type: "email" },
  { id: "phone", label: "PHONE NUMBER *", type: "tel" },
  { id: "company", label: "COMPANY *", type: "text" },
  { id: "country", label: "COUNTRY / LOCATION *", type: "text" },
  { id: "role", label: "PROFESSIONAL ROLE", type: "text" },
  { id: "found_via", label: "HOW DID YOU FIND IDEVRA?", type: "text" },
];

const CountChips = () => (
  <motion.div variants={rise} className="flex flex-wrap gap-3" data-testid="vault-locked-counts">
    <span className="iv-chip">
      <span className="f-orbitron text-[#0284C7]">{String(IDEVRA.patents.items.length).padStart(2, "0")}</span> PATENT RECORDS
    </span>
    <span className="iv-chip">
      <span className="f-orbitron text-[#0284C7]">{String(IDEVRA.trademarks.items.length).padStart(2, "0")}</span> TRADEMARKS
    </span>
    <span className="iv-chip">
      <span className="f-orbitron text-[#94A3B8]">{String(IDEVRA.patents.counts.pipeline).padStart(2, "0")}</span> IN SPECIFICATION
    </span>
  </motion.div>
);

const KeyRedeem = ({ onGranted }) => {
  const [key, setKey] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const redeem = async (e) => {
    e.preventDefault();
    if (!key.trim() || busy) return;
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/api/vault/access/redeem`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ key: key.trim() }),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      const access = { id: d.id, status: "approved", key: d.access_key };
      storeAccess(access);
      sfx.psLaunch();
      onGranted(access);
    } catch {
      sfx.back();
      setErr("INVALID OR UNAPPROVED KEY");
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={redeem} noValidate className="flex w-full max-w-md items-center gap-2">
      <div className="relative flex-1">
        <KeyRound size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
        <input
          type="text"
          data-testid="vault-key-input"
          className="iv-input !pl-10 uppercase"
          placeholder="HAVE AN ACCESS KEY? ENTER IT HERE"
          value={key}
          onChange={(e) => {
            setKey(e.target.value.toUpperCase());
            setErr("");
          }}
        />
      </div>
      <button
        type="submit"
        data-testid="vault-key-submit-btn"
        disabled={busy}
        className="f-syncopate shrink-0 rounded-full border border-[#CBD5E1] px-5 py-3 text-[9px] tracking-[0.25em] text-[#475569] transition-colors hover:border-[#38BDF8] hover:text-[#0284C7] disabled:opacity-50"
      >
        {busy ? "…" : "UNLOCK"}
      </button>
      {err && (
        <span className="f-mono absolute mt-14 text-[8px] tracking-[0.2em] text-[#D97706]" data-testid="vault-key-error">
          {err}
        </span>
      )}
    </form>
  );
};

export default function VaultGate({ initial, onGranted }) {
  const [phase, setPhase] = useState(() =>
    initial?.status === "pending" ? "pending" : initial?.status === "denied" ? "denied" : "form"
  );
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", country: "", role: "", found_via: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const idRef = useRef(initial?.id || null);

  const applyStatus = (status, key) => {
    const access = { id: idRef.current, status, key: key || null };
    storeAccess(access);
    if (status === "approved") {
      sfx.psLaunch();
      onGranted(access);
    } else if (status === "denied") {
      setPhase("denied");
    } else {
      setPhase("pending");
    }
  };

  useEffect(() => {
    if (phase !== "pending" || !idRef.current) return;
    let stop = false;
    const check = async () => {
      try {
        const res = await fetch(`${API}/api/vault/access/status/${idRef.current}`);
        if (!res.ok) return;
        const d = await res.json();
        if (!stop && d.status !== "pending") applyStatus(d.status, d.access_key);
      } catch {
        // offline — retry next tick
      }
    };
    check();
    const iv = setInterval(check, 12000);
    return () => {
      stop = true;
      clearInterval(iv);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const required = ["name", "email", "phone", "company", "country"];
    if (required.some((f) => form[f].trim().length < 2) || !form.email.includes("@")) {
      setErr("COMPLETE ALL REQUIRED FIELDS (*) WITH A VALID EMAIL");
      sfx.back();
      return;
    }
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/api/vault/access/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      const d = await res.json();
      idRef.current = d.id;
      sfx.clank();
      applyStatus(d.status, d.access_key);
    } catch {
      setErr("TRANSMISSION FAILED — TRY AGAIN");
      sfx.back();
    } finally {
      setBusy(false);
    }
  };

  return (
    <motion.div variants={stagger} initial="hidden" animate="show" className="flex flex-col gap-6" data-testid="vault-gate">
      <motion.div variants={rise} className="iv-eyebrow">
        — IP VAULT · {IDEVRA.patents.office.toUpperCase()}
      </motion.div>
      <motion.h2 variants={rise} className="f-orbitron text-xl sm:text-3xl font-bold text-[#0F172A]">
        The vault behind the ventures.
      </motion.h2>
      <CountChips />

      {phase === "form" && (
        <motion.div variants={rise} className="iv-glass max-w-3xl rounded-2xl p-6 sm:p-8" data-testid="vault-request-form">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#0284C7]/10">
              <Lock size={16} color="#0284C7" />
            </span>
            <div>
              <div className="f-orbitron text-sm font-bold text-[#0F172A]">SECURITY CLEARANCE REQUIRED</div>
              <div className="f-mono mt-1 text-[8px] tracking-[0.25em] text-[#64748B]">
                VAULT RECORDS ARE RESTRICTED — REQUEST AN ACCESS KEY FROM THE OPERATOR
              </div>
            </div>
          </div>
          <form onSubmit={submit} noValidate className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
            {FIELDS.map((f) => (
              <input
                key={f.id}
                type={f.type}
                data-testid={`vault-form-${f.id}`}
                className={`iv-input ${f.id === "found_via" ? "sm:col-span-2" : ""}`}
                placeholder={f.label}
                value={form[f.id]}
                onChange={(e) => {
                  setForm((s) => ({ ...s, [f.id]: e.target.value }));
                  setErr("");
                }}
              />
            ))}
            {err && (
              <div className="f-mono sm:col-span-2 text-[9px] tracking-[0.2em] text-[#D97706]" data-testid="vault-form-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              data-testid="vault-form-submit-btn"
              disabled={busy}
              className="f-syncopate sm:col-span-2 rounded-full bg-[#0284C7] px-6 py-3.5 text-[10px] tracking-[0.3em] text-white shadow-[0_0_24px_rgba(56,189,248,0.4)] transition-colors hover:bg-[#0369A1] disabled:opacity-60"
            >
              {busy ? "TRANSMITTING…" : "REQUEST ACCESS KEY"}
            </button>
          </form>
          <div className="mt-6 border-t border-white/70 pt-5">
            <KeyRedeem onGranted={onGranted} />
          </div>
        </motion.div>
      )}

      {phase === "pending" && (
        <motion.div variants={rise} className="iv-glass max-w-3xl rounded-2xl p-6 sm:p-8" data-testid="vault-pending">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#D97706]/10">
              <Hourglass size={16} color="#D97706" className="animate-pulse" />
            </span>
            <div>
              <div className="f-orbitron text-sm font-bold text-[#0F172A]">CLEARANCE PENDING</div>
              <div className="f-mono mt-1 text-[8px] tracking-[0.25em] text-[#64748B]">
                YOUR REQUEST IS AWAITING OPERATOR APPROVAL — THIS SCREEN UNLOCKS AUTOMATICALLY
              </div>
            </div>
          </div>
          <p className="mt-4 max-w-xl text-[12px] leading-relaxed text-[#64748B]">
            Once approved, the vault opens on this device the next time you visit — and your access key
            is emailed to you as a backup for other devices.
          </p>
          <div className="mt-6 border-t border-white/70 pt-5">
            <KeyRedeem onGranted={onGranted} />
          </div>
        </motion.div>
      )}

      {phase === "denied" && (
        <motion.div variants={rise} className="iv-glass max-w-3xl rounded-2xl p-8 text-center" data-testid="vault-denied">
          <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-[#DC2626]/10">
            <ShieldAlert size={20} color="#DC2626" />
          </span>
          <div className="f-orbitron mt-4 text-lg font-black text-[#0F172A]">ACCESS RESTRICTED</div>
          <p className="f-mono mx-auto mt-3 max-w-md text-[9px] leading-relaxed tracking-[0.2em] text-[#64748B]">
            YOUR VAULT ACCESS REQUEST WAS NOT APPROVED. FOR FURTHER ENQUIRIES, CONTACT THE OPERATOR VIA OPEN COMMS.
          </p>
        </motion.div>
      )}

      <motion.div variants={rise} className="flex items-center gap-2 f-mono text-[8px] tracking-[0.3em] text-[#B6C2D1]">
        <ShieldCheck size={12} /> RECORDS SECURED · OPERATOR-APPROVED ACCESS ONLY
      </motion.div>
    </motion.div>
  );
}
